import { Component, Input, OnDestroy } from '@angular/core';
import { CheckboxChangeEvent, HcTableDataSource } from '@healthcatalyst/cashmere';
import { Subscription, takeUntil } from 'rxjs';
import BaseComponent from '../base.component';

export interface SelectableItem {
    isSelected: boolean;
    unselectable?: boolean;
}

@Component({
    selector: 'cdp-grid-bulk-actions',
    templateUrl: './grid-bulk-actions.component.html',
    standalone: false
})
export class GridBulkActionsComponent<Type extends SelectableItem> extends BaseComponent implements OnDestroy {
    @Input() hideRowCount: boolean;
    /** If row count is hidden, this can be used to force display the 'select all' message */
    @Input() forceSelectAllMessage: boolean;
    @Input() set dataSource(dataSource: HcTableDataSource<Type>) {
        // prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this._dataSource = dataSource;
        });
    }

    get dataSource(): HcTableDataSource<Type> {
        return this._dataSource;
    }
    private _dataSource: HcTableDataSource<Type>;

    public get allChecked(): boolean {
        return (
            this.selectedRowsCountDisplayed === this.dataSource?.filteredData.length &&
            this.selectedRowsCountDisplayed > 0
        );
    }
    public get selectedRowCount(): number {
        return this.selectedRows.length || 0;
    }
    public get masterCheckboxIsIndeterminate(): boolean {
        return this.selectedRowCount !== 0 && !this.allChecked;
    }

    public get selectedRows(): Type[] {
        return this.dataSource ? this.dataSource?.data.filter(i => i.isSelected) : [];
    }

    private get selectedRowsCountDisplayed(): number {
        return this.dataSource?.filteredData.filter(i => i.isSelected).length || 0;
    }

    public get selectedRowsHidden(): number {
        return this.selectedRowCount - this.selectedRowsCountDisplayed;
    }

    public presentSelectAllPagesOption = false;
    private _paginatorSubscription?: Subscription;

    override ngOnDestroy(): void {
        this._paginatorSubscription = undefined;
        super.ngOnDestroy();
    }

    /** Set a single item in the data source to be selected or not. */
    public toggleSelectItem(event: CheckboxChangeEvent, item: Type): void {
        this._toggleSelectItem(event.checked, item);
        this.updateSelectAllPagesOption();
    }

    /** Sets all filtered items in the data source to be selected or not. */
    public toggleAllSelectItems(shouldSelect: boolean): void {
        this.dataSource?.filteredData.forEach((item: Type) => {
            this._toggleSelectItem(shouldSelect, item);
        });
        this.updateSelectAllPagesOption();
    }

    public onMasterCheckboxClicked(event: CheckboxChangeEvent): void {
        const paginator = this.dataSource?.paginator;
        if (!paginator || !event.checked) {
            this.toggleAllSelectItems(event.checked);
            return;
        }

        const data = this.dataSource.sort
            ? this.dataSource.sortData(this.dataSource.filteredData, this.dataSource.sort)
            : this.dataSource.filteredData;

        data.slice(
            (paginator.pageNumber - 1) * paginator.pageSize,
            Math.min(paginator.pageNumber * paginator.pageSize, this.dataSource.filteredData.length)
        ).forEach((item: Type) => {
            this._toggleSelectItem(event.checked, item);
        });
        this.updateSelectAllPagesOption();
    }

    /** Check to see if we should present the option to select all rows on all pages */
    public updateSelectAllPagesOption(): void {
        const paginator = this.dataSource?.paginator;
        if (!paginator) {
            return;
        }
        this.setupPaginatorSubscriptionAsNeeded();
        const itemsOnPage = this.dataSource?.data.slice(
            (paginator.pageNumber - 1) * paginator.pageSize,
            Math.min(paginator.pageNumber * paginator.pageSize, this.dataSource.data.length)
        );
        this.presentSelectAllPagesOption =
            itemsOnPage.every(i => i.isSelected) &&
            itemsOnPage.length < this.dataSource.data.length &&
            this.selectedRowCount !== this.dataSource.data.length;
    }

    private _toggleSelectItem(shouldSelect: boolean, item: Type): void {
        if (!item.unselectable) {
            item.isSelected = shouldSelect;
        }
    }

    private setupPaginatorSubscriptionAsNeeded(): void {
        if (!this._paginatorSubscription) {
            this._paginatorSubscription = this._dataSource?.paginator?.page
                .pipe(takeUntil(this.unsubscribe))
                .subscribe(() => {
                    this.updateSelectAllPagesOption();
                });
        }
    }
}
